import React, { useEffect, useState, useContext } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import ApprovalStatus from '../constants/ApprovalStatus';
import RentalStatus from '../constants/RentalStatus';
import BoardSlug from '../constants/BoardSlug';
import UnitType from '../constants/UnitType';
import BookingCalendarModal from '../components/modal/BookingCalendarModal';

import BannerImg from '../custom/images/banner.png';
import LoginIconImg from '../custom/images/login.svg';
import MypageIconImg from '../custom/images/mypage.svg';
import PlusIconImg from '../custom/images/plus.svg';

import * as api from '../apis';
import { convertDateToStr, getRoomBookingDate, getProductBookingDate } from '../utils';

const Main = () => {
	const navigate = useNavigate();
	const { user } = useContext(UserContext);

	const [noticeBoard, setNoticeBoard] = useState(null);
	const [postList, setPostList] = useState(null);
	const [bookingList, setBookingList] = useState(null);
	const [bookingCalendarModalData, setBookingCalendarModalData] = useState({ show: false });

	useEffect(() => {
		if (user) {
			let filterParams = [`page_size=2`];
			api.getMyBooking(filterParams).then((res) => {
				if (res && res.data) {
					setBookingList(res.data);
				}
			});
		}

		api.getBoards().then((res) => {
			if (res && res.data) {
				let noticeBoard = res.data.find((board) => board.slug === BoardSlug.NOTICE);
				setNoticeBoard(noticeBoard);
				api.getPosts(noticeBoard.id, [`page_size=4`]).then((res) => {
					if (res && res.data) {
						setPostList(res.data.items);
					}
				});
			}
		});
	}, [user]);

	const onClickLogin = () => {
		window.location =
			'https://care.shareschool.ac.kr/xn-sso/gw.php?login_type=standalone&site=studio&callback_url=https%3A%2F%2Freservation.shareschool.ac.kr%2Flogin%2Fcallback';
	};

	const onClickMypage = () => {
		navigate('/mypage');
	};

	const onClickNoticeBoard = () => {
		navigate('/boards/notice');
	};

	const onClickPostContents = (postId) => {
		navigate(`/boards/${noticeBoard.id}/posts/${postId}`);
	};

	const renderBoardContentsRow = (post, idx) => {
		return (
			<BoardContentsRow key={idx} onClick={() => onClickPostContents(post.id)}>
				<BoardCategoryText type={`category-${post.category?.id ?? 0}`}>
					{post.category?.name ?? '-'}
				</BoardCategoryText>
				<BoardContentTitle>{post.title}</BoardContentTitle>
				<BoardContentDate>
					{post.created_at ? convertDateToStr(new Date(post.created_at)) : '-'}
				</BoardContentDate>
			</BoardContentsRow>
		);
	};

	const renderMyBooking = () => {
		if (!bookingList || bookingList.items.length === 0) {
			return <NoBookingList>예약 내역이 없습니다.</NoBookingList>;
		}
		return (
			<MyBookingContainer>
				{bookingList?.items.map((booking, idx) => {
					const bookingUnitType = booking.unit.unit_type_name;
					const locationDetail =
						bookingUnitType === UnitType.ROOM
							? `${booking.unit.name} ${booking.room_item?.name}`
							: `${booking.unit.name}`;
					const bookingDate =
						bookingUnitType === UnitType.PRODUCT
							? getProductBookingDate(booking.booking_start_date, booking.booking_end_date).split('~')[0]
							: getRoomBookingDate(booking.booking_start_date, booking.booking_end_date).split('~')[0];
					return renderBookingDetailRow(
						idx,
						bookingUnitType,
						booking.approval_status,
						booking.rental_status,
						bookingDate,
						locationDetail
					);
				})}
			</MyBookingContainer>
		);
	};

	const renderBookingDetailRow = (idx, type, approvalStatus, rentalStatus, calendarDetail, locationDetail) => {
		return (
			<MyContentsRow key={idx}>
				<BookingStatusBox status={approvalStatus}>
					<span>{ApprovalStatus.toString(approvalStatus)}</span>
				</BookingStatusBox>
				<BookingDetailContainer>
					<BookingDetailRow>
						<BookingType type={type}>{UnitType.toString(type)}</BookingType>
						{type === UnitType.PRODUCT && (
							<RentalStatusBox status={rentalStatus}>
								{RentalStatus.toString(rentalStatus)}
							</RentalStatusBox>
						)}
					</BookingDetailRow>
					<BookingDetailRow column>
						<BookingDetailText>{calendarDetail ?? '-'}</BookingDetailText>
						<BookingLocationDetailText>{locationDetail ?? '-'}</BookingLocationDetailText>
					</BookingDetailRow>
				</BookingDetailContainer>
			</MyContentsRow>
		);
	};

	return (
		<Container>
			<BannerBackground src={BannerImg}>
				<BannerContainer>
					<BoardContainer>
						<BoardTopContainer>
							<BoardTopSubTitle>Center for AI robotics Education</BoardTopSubTitle>
							<BoardTopTitle>
								로봇직업교육센터는
								<BoardTopTitleBlue>{' 국내 최고 수준'}</BoardTopTitleBlue>의
								<br />
								로봇SI 현장실무인재를 양성합니다.
							</BoardTopTitle>
						</BoardTopContainer>
						<BoardContentsContainer>
							<BoardTitleContainer>
								<BoardSubTitle>공지사항</BoardSubTitle>
								<PlusButtonIcon src={PlusIconImg} onClick={() => onClickNoticeBoard()} />
							</BoardTitleContainer>
							<BoardContentsRowContainer>
								{postList?.map((post, idx) => {
									return renderBoardContentsRow(post, idx);
								})}
							</BoardContentsRowContainer>
						</BoardContentsContainer>
					</BoardContainer>
					<MyContentContainer>
						<MyContainer>
							{user ? (
								<>
									<MyTopContainerLogin>
										<MyStatusTitle>내 예약현황</MyStatusTitle>
										{renderMyBooking()}
									</MyTopContainerLogin>
									<MyBottomContainer>
										<LoginButton onClick={onClickMypage}>
											<LoginButtonText>마이페이지</LoginButtonText>
											<LoginButtonIcon src={MypageIconImg} />
										</LoginButton>
									</MyBottomContainer>
								</>
							) : (
								<>
									<MyTopContainerLogout>
										<MyStatusTitle>내 예약현황</MyStatusTitle>
										<MyStatusLoginInfo>로그인 후 이용해 주세요.</MyStatusLoginInfo>
									</MyTopContainerLogout>
									<MyBottomContainer>
										<LoginButton onClick={onClickLogin}>
											<LoginButtonText>로그인</LoginButtonText>
											<LoginButtonIcon src={LoginIconImg} />
										</LoginButton>
									</MyBottomContainer>
								</>
							)}
						</MyContainer>
						{user && (
							<MyBottomButtonContainer>
								<MyBookingCalendarButton onClick={() => setBookingCalendarModalData({ show: true })}>
									나의 예약 캘린더
								</MyBookingCalendarButton>
							</MyBottomButtonContainer>
						)}
					</MyContentContainer>
				</BannerContainer>
			</BannerBackground>
			{bookingCalendarModalData.show && (
				<BookingCalendarModal onClose={() => setBookingCalendarModalData({ show: false })} />
			)}
		</Container>
	);
};

const Container = styled.div`
	width: 100%;
	height: calc(100vh - 270px);

	@media only screen and (max-width: 767.98px) {
		height: 100%;
	}
`;

const BannerBackground = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;

	position: relative;
	width: 100%;
	height: 100%;
	top: 0px;

	${(props) =>
		props.src &&
		css`
			background-image: url(${props.src});
			background-repeat: no-repeat;
			background-size: cover;
			background-position: center center;
		`}

	@media only screen and (max-width: 767.98px) {
		width: 100%;
		height: auto;
	}
`;

const BannerContainer = styled.div`
	width: 100%;
	max-width: 1240px;

	display: flex;
	justify-content: space-between;
	align-items: center;

	@media only screen and (max-width: 767.98px) {
		flex-direction: column;
		justify-content: flex-end;
	}
`;

const BoardContainer = styled.div`
	width: 592px;

	@media only screen and (max-width: 767.98px) {
		width: 100%;
		margin-top: 11px;
	}
`;

const BoardTopContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 15px;

	@media only screen and (max-width: 767.98px) {
		width: 100%;
		height: 343px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	@media only screen and (max-width: 414.98px) {
		height: 220px;
	}
`;

const BoardTopSubTitle = styled.div`
	font-size: 20px;
	font-family: 'Pretendard';
	line-height: 28px;
	letter-spacing: 0.47em;
	opacity: 0.6;
	color: #fff;

	@media only screen and (max-width: 767.98px) {
		font-size: 12px;
		line-height: 20px;
	}
`;

const BoardTopTitle = styled.div`
	font-size: 40px;
	font-weight: 600;
	line-height: 48px;
	font-family: 'Pretendard';
	color: #fff;

	@media only screen and (max-width: 767.98px) {
		text-align: center;
	}

	@media only screen and (max-width: 533.98px) {
		font-size: 24px;
		line-height: 31.03px;
	}
`;

const BoardTopTitleBlue = styled.span`
	color: #30bce2;
`;

const BoardTitleContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	@media only screen and (max-width: 767.98px) {
		padding: 0 0 15px 0;
	}
`;

const BoardSubTitle = styled.span`
	font-weight: 600;
	font-size: 24px;
	line-height: 28.64px;
	color: #ffffff;
`;

const BoardContentsContainer = styled.div`
	border-top: 1px solid #ffffffb3;
	margin-top: 40px;
	width: 100%;
	display: flex;
	padding-top: 20px;
	box-sizing: border-box;
	flex-direction: column;

	@media only screen and (max-width: 767.98px) {
		width: 100%;
		height: auto;
		padding: 20px;
	}
`;

const PlusButtonIcon = styled.img`
	width: 24px;
	height: 24px;
	object-fit: contain;
	cursor: pointer;

	@media only screen and (max-width: 767.98px) {
		width: 24px;
		height: 24px;
		object-fit: contain;
	}
`;

const BoardContentsRowContainer = styled.div`
	display: flex;
	min-height: 108px;
	flex-direction: column;
	gap: 8px;
	margin-top: 16px;
`;

const BoardContentsRow = styled.div`
	position: relative;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	flex-shrink: 0;
	box-sizing: border-box;
	cursor: pointer;

	@media only screen and (max-width: 767.98px) {
		width: 100%;
		height: auto;
		padding: 0;
		border: none;

		& + & {
			margin-left: 0;
			margin-top: 10px;
		}
	}
`;

const BoardCategoryText = styled.span`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 500;
	font-size: 16px;
	color: #30bce2;
	line-height: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 58px;
`;

const BoardContentTitle = styled.span`
	flex: 1;
	margin-left: 10px;
	font-family: 'Pretendard';
	font-style: normal;
	font-size: 18px;
	line-height: 21px;
	color: #ffffff;
	overflow: hidden;
	cursor: pointer;
	white-space: nowrap;
	text-overflow: ellipsis;

	@media only screen and (max-width: 767.98px) {
		width: 100%;
		height: auto;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`;

const BoardContentDate = styled.span`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 17px;
	color: #ffffffcc;

	@media only screen and (max-width: 767.98px) {
		margin-top: 1px;
	}
`;

const MyContentContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 16px;

	@media only screen and (max-width: 767.98px) {
		width: 100%;
	}
`;

const MyContainer = styled.div`
	width: 300px;
	height: 387px;
	box-sizing: border-box;
	border-radius: 4px;
	padding: 32px 28px;
	background-color: #ffffff;
	display: flex;
	flex-direction: column;
	justify-content: center;

	@media only screen and (max-width: 767.98px) {
		width: 100%;
		height: auto;
		padding: 30px 20px;
		border-radius: 0;
		background-color: white;
	}
`;

const MyTopContainerLogin = styled.div`
	margin-top: 0;
`;

const MyTopContainerLogout = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 10px;

	@media only screen and (max-width: 767.98px) {
		height: auto;
	}
`;

const NoBookingList = styled.div`
	margin-top: 20px;
	display: flex;
	width: 100%;
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 600;
	font-size: 16px;
	line-height: 19px;
	color: #ffffff;
`;

const MyBookingContainer = styled.div`
	margin-top: 40px;

	@media only screen and (max-width: 767.98px) {
		margin-top: 25px;
	}
`;

const MyContentsRow = styled.div`
	height: 73px;
	display: flex;

	& + & {
		margin-top: 15px;
	}
`;

const BookingStatusBox = styled.div`
	width: 73px;
	height: 73px;
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 36px;
	border: 8px solid #30bce2;

	span {
		width: 30px;
		text-align: center;
		font-family: 'Pretendard';
		font-style: normal;
		font-weight: 600;
		font-size: 16px;
		line-height: 19px;
		color: #30bce2;
	}
`;

const BookingDetailContainer = styled.div`
	margin-left: 20px;
	display: flex;
	flex-direction: column;
	flex: 1;
	justify-content: center;
`;

const BookingDetailRow = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;

	& + & {
		margin-top: 8px;
	}

	${(props) =>
		props.column &&
		css`
			flex-direction: column;
			align-items: flex-start;
		`}
`;

const BookingType = styled.div`
	width: 49px;
	height: 26px;
	border-radius: 50px;
	display: flex;
	align-items: center;
	justify-content: center;

	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 600;
	font-size: 14px;
	line-height: 17px;

	${(props) => {
		switch (props.type) {
			case UnitType.ROOM:
				return css`
					border: 1px solid #0085ce;
					color: #0085ce;
				`;
			case UnitType.PRODUCT:
				return css`
					border: 1px solid #fc9639;
					color: #fc9639;
				`;
			default:
				return css`
					border: 1px solid #0085ce;
					color: #0085ce;
				`;
		}
	}};
`;

const RentalStatusBox = styled.div`
	margin-left: 5px;
	width: 70px;
	height: 26px;
	border-radius: 50px;
	display: flex;
	align-items: center;
	justify-content: center;

	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 600;
	font-size: 14px;
	line-height: 17px;
	color: #ffffff;

	${(props) => {
		switch (props.status) {
			case RentalStatus.RENTAL_READY:
				return css`
					background-color: #00c6ba;
				`;
			case RentalStatus.RENTAL_DELAY:
				return css`
					background-color: #e7683f;
				`;
			case RentalStatus.RETURN_DELAY:
				return css`
					background-color: #ff3535;
				`;
			case RentalStatus.RENTAL_COMPLETE:
			case RentalStatus.RETURN_COMPLETE:
				return css`
					background-color: #0d93f4;
				`;
			default:
				return css`
					background-color: #00c6ba;
				`;
		}
	}};
`;

const BookingDetailText = styled.span`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 17px;
`;

const BookingLocationDetailText = styled.span`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 600;
	font-size: 16px;
	line-height: 19px;

	width: 150px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const MyBottomContainer = styled.div`
	width: 100%;
	height: 65px;
	margin-top: 20px;

	@media only screen and (max-width: 767.98px) {
		padding-left: 0;
	}
`;

const LoginButton = styled.div`
	width: 100%;
	height: 70px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-radius: 4px;
	padding: 0 30px;
	cursor: pointer;
	background-color: #114174;

	&:hover {
		background-color: #09509c;
	}
`;

const LoginButtonText = styled.span`
	font-size: 22px;
	line-height: 26.25px;
	color: #ffffff;
`;

const LoginButtonIcon = styled.img``;

const MyStatusTitle = styled.span`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 700;
	font-size: 32px;
	line-height: 38px;

	@media only screen and (max-width: 767.98px) {
		margin-top: 0;
		font-size: 24px;
		line-height: 29px;
	}
`;

const MyStatusLoginInfo = styled.span`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 500;
	font-size: 16px;
	line-height: 19px;

	@media only screen and (max-width: 767.98px) {
		margin-top: 0;
		font-size: 14px;
		line-height: 17px;
	}
`;

const MyBottomButtonContainer = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 10px;

	@media only screen and (max-width: 767.98px) {
		padding: 0 20px 20px 20px;
	}
`;

const MyBottomButton = styled.div`
	width: 100%;
	padding: 0 13px;
	height: 35px;
	border-radius: 4px;
	color: #ffffff;
	font-size: 16px;
	font-weight: 500;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: 'Pretendard';

	@media only screen and (max-width: 767.98px) {
		flex: 1 1 auto;
	}
`;

const MyBookingCalendarButton = styled(MyBottomButton)`
	background-color: #30bce2;
	outline: none;
	border: none;
	cursor: pointer;
	flex-shrink: 0;

	&:hover {
		background-color: #00a7d4;
	}

	@media only screen and (max-width: 767.98px) {
		flex: 1 1 auto;
	}
`;

export default Main;
